import type { Header } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { ForwardedRef, MouseEvent, ReactElement } from 'react';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { TableCell, TableHeaderCell, TableRow } from '../parts';
import { TableCaption } from '../parts/TableCaption/TableCaption';
import { TABLE_CELL_STYLES } from '../parts/TableCell/styles';
import { TableBaseAPI, TableBaseProps } from './types';

import { Loadable } from '@/components/loaders';

const TableBaseComp = <T extends { id?: string }>(
  {
    isLoading = false,
    className,
    tableClassName,
    innerWrapperClassName,
    table,
    footer,
    onScroll,
    renderFooter,
    rowHeight = 72,
    notFound = 'No Data',
    onRowClick,
    caption,
    pagination,
  }: TableBaseProps<T>,
  ref: ForwardedRef<TableBaseAPI>
) => {
  const innerRef = useRef<HTMLDivElement>(null);

  const onHeaderClick = (e: MouseEvent, header: Header<T, unknown>) => {
    const sortingHandler = header.column.getToggleSortingHandler();
    if (sortingHandler) sortingHandler(e);
  };

  const rows = table.getRowModel().rows;

  const { getTotalSize, getVirtualItems, scrollToOffset } = useVirtualizer({
    count: rows.length,
    getScrollElement: () => innerRef.current,
    estimateSize: () => rowHeight,
    overscan: 10,
  });

  const totalSize = getTotalSize();
  const virtualItems = getVirtualItems();

  useImperativeHandle(
    ref,
    () => {
      return {
        reactTableRef: innerRef,
        scrollToOffset,
      };
    },
    []
  );

  const paddingTop = virtualItems.length > 0 ? virtualItems?.[0]?.start || 0 : 0;
  const paddingBottom = virtualItems.length > 0 ? totalSize - (virtualItems?.[virtualItems.length - 1]?.end || 0) : 0;

  return (
    <>
      <div className={twMerge('flex w-full flex-1 flex-col justify-between overflow-hidden bg-inherit', className)}>
        <div className='relative flex flex-1 overflow-hidden'>
          <div className={twMerge('relative flex flex-1 flex-col overflow-y-auto pb-2', innerWrapperClassName)} ref={innerRef} onScroll={onScroll}>
            <table className={twMerge('relative w-full border-separate border-spacing-0', tableClassName)}>
              {caption ? <TableCaption rowHeight={rowHeight}>{caption}</TableCaption> : null}
              <thead className='group/header sticky left-0 top-0 z-10 bg-secondary-background'>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return <TableHeaderCell<T> key={header.id} header={header} onHeaderClick={onHeaderClick} />;
                    })}
                  </tr>
                ))}
              </thead>
              <tbody className='relative' style={{ height: `${getTotalSize()}px` }}>
                {paddingTop > 0 && (
                  <tr>
                    <td style={{ height: `${paddingTop}px` }} />
                  </tr>
                )}
                {virtualItems.map((virtualRow) => {
                  const row = rows[virtualRow.index];

                  return (
                    <TableRow<T>
                      key={virtualRow.key}
                      index={virtualRow.index}
                      row={row}
                      style={{
                        height: `${virtualRow.size}px`,
                      }}
                      onClick={onRowClick}
                    >
                      {row.getVisibleCells().map((cell) => {
                        return <TableCell<T> key={cell.id} cell={cell} />;
                      })}
                    </TableRow>
                  );
                })}
                {pagination && (
                  <TableRow index={virtualItems.length + 1}>
                    <td colSpan={table.getFlatHeaders().length} className={TABLE_CELL_STYLES}>
                      {pagination}
                    </td>
                  </TableRow>
                )}

                {paddingBottom > 0 && (
                  <tr>
                    <td style={{ height: `${paddingBottom}px` }} />
                  </tr>
                )}
              </tbody>
              {!!renderFooter && <tfoot>{renderFooter}</tfoot>}
            </table>
            {isLoading || rows.length === 0 ? (
              <div className='rounded-b-lg border border-t-0 border-secondary-border bg-white-100 p-5 text-center text-sm text-tertiary-600'>
                <Loadable isLoading={isLoading}>{notFound}</Loadable>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {footer}
    </>
  );
};

export const TableBase = forwardRef(TableBaseComp) as <T>(props: TableBaseProps<T> & { ref?: ForwardedRef<TableBaseAPI> }) => ReactElement;
